import dynamic from 'next/dynamic';
import NextLink from 'next/link';

import Scale from '@/icons/light/Scale.svg';
import Bathtub from '@/icons/light/bathtub.svg';
import Bedroom from '@/icons/light/bedroom.svg';
import Garaje from '@/icons/light/garaje.svg';
import Mueble from '@/icons/light/mueble.svg';
import Otros from '@/icons/light/otros.svg';
import Piscina from '@/icons/light/piscina.svg';
import Sun from '@/icons/light/sun.svg';
import { Container, Link } from '@/atoms';
import { Gallery } from '@/molecules';
import { FlatDescription } from '@/organisms';

import Background from '@/assets/images/background.jpg';
import Zone from '@/assets/images/zonas.jpg';
import { flatName, zonePath } from '@/components/algolia/utils';
import { formatNumberToCurrency } from '@/utils';

import type { TypeFlatAnalytics } from '@/types';
import type { FunctionComponent } from 'react';
import type { TypeFlatSheetProps } from './types';

const FormsTabs = dynamic(() => import('@/organisms/forms-tabs'), {
  ssr: true,
});

/**
 * FlatDescription
 */
export const FlatSheet: FunctionComponent<TypeFlatSheetProps> = ({
  property,
}) => {
  const navigateToFirstHit = () =>
    zonePath(property?.attributes, property?.attributes?.ventaAlquiler);
  const fotosToDisplay = property?.attributes?.fotos
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    ?.map((itm) =>
      itm.url
        ? {
            src: itm.url,
            alt: `${property?.attributes?.tipoDeUsoDescription} de ${
              property?.attributes?.habitaciones
            } ${
              (property?.attributes?.habitaciones as number) > 1
                ? 'habitaciones'
                : 'habitación'
            } en ${
              (property?.attributes?.ciudad as string)[0].toUpperCase() +
              (property?.attributes?.ciudad as string)
                .substring(1)
                .toLowerCase()
            }`,
          }
        : null
    )
    .filter(Boolean);
  const isForSale =
    property?.attributes?.ventaAlquiler?.toLowerCase() === 'venta';

  return property ? (
    <>
      <Container wrapper>
        <div className="flex justify-between">
          <NextLink
            href={navigateToFirstHit()}
            passHref
            prefetch={false}
            legacyBehavior
          >
            <Link icon="left">Listado</Link>
          </NextLink>
        </div>
      </Container>

      <Container wrapper padding="px-4">
        <Gallery
          flat={{
            codigoUnidadAlquiler: property?.attributes
              ?.codigoUnidadAlquiler as string,
            ventaAlquiler: property?.attributes
              ?.ventaAlquiler as TypeFlatAnalytics['ventaAlquiler'],
            reservable: property?.attributes?.reservable,
            visitable: property?.attributes?.visitable,
            nombreUE: property?.attributes?.nombreUE as string,
            cityArea: property?.attributes?.tipoZona?.infoAdicional,
          }}
          images={
            !fotosToDisplay || (fotosToDisplay && fotosToDisplay.length === 0)
              ? [
                  {
                    alt: 'Imagen de ejemplo',
                    src: Background.src,
                  },
                  { src: Zone.src, alt: 'ejemplo2' },
                  { src: Background.src, alt: 'ejemplo3' },
                ]
              : fotosToDisplay.length === 1
              ? [
                  ...fotosToDisplay,
                  { src: Zone.src, alt: 'ejemplo2' },
                  { src: Background.src, alt: 'ejemplo3' },
                ]
              : fotosToDisplay.length === 2
              ? [...fotosToDisplay, { src: Background.src, alt: 'ejemplo3' }]
              : fotosToDisplay
          }
          video={property?.attributes?.video?.infoAdicional}
          typeModal="gallery"
        />
      </Container>

      <Container
        wrapper
        justify="justify-around"
        classNameWrapper="!flex-row gap-6"
      >
        <FlatDescription
          {...(property?.attributes?.virtualView?.infoAdicional && {
            has360: true,
            src360: property?.attributes?.virtualView?.infoAdicional,
          })}
          {...(property?.attributes?.video?.infoAdicional && {
            hasVideo: true,
            srcVideo: property?.attributes?.video?.infoAdicional,
          })}
          title={flatName(property?.attributes)}
          price={formatNumberToCurrency(
            isForSale
              ? (property?.attributes?.importeVenta as number)
              : (property?.attributes?.importeRecibofinal as number)
          )}
          isPeriodic={!isForSale}
          description={{
            title: 'Descripción del alojamiento',
            content:
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              property?.attributes?.informacionGeneral?.argumentario
                ?.replaceAll('##', '<br><br>')
                ?.replaceAll('•,,', '<br>&nbsp;•&nbsp;&nbsp;&nbsp;'),
          }}
          {...(property?.attributes?.otrosDatos?.argumentario && {
            otrosDatos: property.attributes.otrosDatos.argumentario
              ?.replaceAll('##', '<br><br>')
              ?.replaceAll('•,,', '<br>&nbsp;•&nbsp;&nbsp;&nbsp;'),
          })}
          flat={{
            codigoUnidadAlquiler: property?.attributes
              ?.codigoUnidadAlquiler as string,
            ventaAlquiler: property?.attributes?.ventaAlquiler as string,
            reservable: property?.attributes?.reservable as string,
            visitable: property?.attributes?.visitable as string,
            nombreUE: property?.attributes?.nombreUE as string,
          }}
          features={{
            title: 'Características',
            features: property?.attributes?.caracteristicas,
          }}
          highlightedFeatures={[
            ...(property?.attributes?.metrosComputablesUA
              ? [
                  {
                    Icon: Scale,
                    value: `${property?.attributes?.metrosComputablesUA} m2`,
                  },
                ]
              : []),
            ...(property?.attributes?.habitaciones
              ? [
                  {
                    Icon: Bedroom,
                    value: `${property?.attributes?.habitaciones} Habs.`,
                  },
                ]
              : []),
            ...(property?.attributes?.banos
              ? [
                  {
                    Icon: Bathtub,
                    value: `${property?.attributes?.banos} baños`,
                  },
                ]
              : []),
            ...(property?.attributes?.hasAmueblado !== 'No'
              ? [
                  {
                    Icon: Mueble,
                    value: 'Amueb.',
                  },
                ]
              : []),
            ...(property?.attributes?.hasPiscina !== 'No'
              ? [
                  {
                    Icon: Piscina,
                    value: 'Piscina',
                  },
                ]
              : []),
            ...(property?.attributes?.hasGaraje !== 'No'
              ? [
                  {
                    Icon: Garaje,
                    value: 'Garaje',
                  },
                ]
              : []),
            ...(property?.attributes?.hasTerrazas !== 'No'
              ? [
                  {
                    Icon: Sun,
                    value: 'Terraza',
                  },
                ]
              : []),
            ...(property?.attributes?.hasTraseros !== 'No'
              ? [
                  {
                    Icon: Otros,
                    value: 'Trastero',
                  },
                ]
              : []),
          ]}
          {...(property?.attributes?.latitud && property?.attributes?.longitud
            ? {
                location: {
                  title: 'Ubicación',
                  location: {
                    lat: parseFloat(property?.attributes?.latitud as string),
                    lng: parseFloat(property?.attributes?.longitud as string),
                  },
                },
              }
            : {})}
          availableDates={property.attributes.fechasDisponibles}
        />

        <div className="rounded-custom hidden h-full w-[450px] flex-shrink-0 bg-neutrals-50 p-6 lg:sticky lg:top-6 lg:z-10 lg:block">
          <FormsTabs
            flat={{
              codigoUnidadAlquiler: property?.attributes?.codigoUnidadAlquiler,
              ventaAlquiler: property?.attributes?.ventaAlquiler,
              reservable: property?.attributes?.reservable,
              visitable: property?.attributes?.visitable,
              nombreUE: property?.attributes?.nombreUE,
              cityArea: property?.attributes?.tipoZona?.infoAdicional,
              name: flatName(property?.attributes),
              maxOcupantes: property?.attributes?.maxOcupantes,
            }}
          />
        </div>
      </Container>
    </>
  ) : null;
};

FlatSheet.displayName = 'FlatSheet';
